import { DateTime } from "luxon";

export interface LaneFormState {
  id: string;
  name: string;
  equipmentTypes: string[];
  services: string[];
  originProvinces: string[];
  destinationProvinces: string[];
  hasExpiry: boolean;
  expiresOn: string;
  error: string | null;
}

export interface LaneWriteRequestBody {
  name: string;
  equipment_types: string[] | null;
  services: string[] | null;
  origin_provinces: string[] | null;
  destination_provinces: string[] | null;
  expires_at: string | null;
}

// Lane expiry is a UTC instant. A carrier picks a local calendar day, and the
// lane keeps matching until the end of that day in the carrier's timezone.
export function localDateToEndOfDayISOString(localDate: string): string {
  return DateTime.fromISO(localDate).endOf("day").toUTC().toISO() ?? "";
}

export function getTodayLocalDateString(): string {
  return DateTime.now().toISODate() ?? "";
}

export function ISOStringToLocalDateString(ISOTimestamp: string): string {
  return DateTime.fromISO(ISOTimestamp).toISODate() ?? "";
}

export const createEmptyLaneForm = (): LaneFormState => ({
  id: `${Date.now()}-${Math.random()}`,
  name: "",
  equipmentTypes: [],
  services: [],
  originProvinces: [],
  destinationProvinces: [],
  hasExpiry: false,
  expiresOn: "",
  error: null,
});

export function laneToFormState(lane: ShipmentSearch): LaneFormState {
  // A lapsed date is prefilled like any other. Blanking it would block saving an edit
  // that never touched the expiry, and the only way out of that is switching the expiry
  // off -- which silently puts a retired lane back into matching.
  return {
    id: lane.id.toString(),
    name: lane.name,
    equipmentTypes: lane.equipment_types ?? [],
    services: lane.services ?? [],
    originProvinces: lane.origin_provinces ?? [],
    destinationProvinces: lane.destination_provinces ?? [],
    hasExpiry: Boolean(lane.expires_at),
    expiresOn: lane.expires_at
      ? ISOStringToLocalDateString(lane.expires_at)
      : "",
    error: null,
  };
}

// The name is resolved by the caller because creating auto-names a blank lane
// while editing requires the carrier to supply one.
export function laneFormToRequestBody(
  form: LaneFormState,
  name: string,
): LaneWriteRequestBody {
  return {
    name,
    equipment_types: form.equipmentTypes.length ? form.equipmentTypes : null,
    services: form.services.length ? form.services : null,
    origin_provinces: form.originProvinces.length ? form.originProvinces : null,
    destination_provinces: form.destinationProvinces.length
      ? form.destinationProvinces
      : null,
    expires_at:
      form.hasExpiry && form.expiresOn
        ? localDateToEndOfDayISOString(form.expiresOn)
        : null,
  };
}

/*
The stored instant wins whenever the carrier left the day alone. A lane's expiry need
not sit at the end of a local day -- anything not minted by this form (an admin fix, a
future API client) does not -- and re-deriving it from the day would move the instant,
which the backend then rejects as a newly proposed past date.
*/
export function resolveEditedExpiresAt(
  form: LaneFormState,
  lane: ShipmentSearch,
): string | null {
  if (!form.hasExpiry || !form.expiresOn) return null;
  if (
    lane.expires_at &&
    form.expiresOn === ISOStringToLocalDateString(lane.expires_at)
  ) {
    return lane.expires_at;
  }
  return localDateToEndOfDayISOString(form.expiresOn);
}

/*
Owns both the save rules and their copy so the create and edit modals cannot drift
apart. Only editing requires a name, because creating auto-names a blank lane.
*/
export function getLaneFormBlockedReason(
  form: LaneFormState,
  nameRequired: boolean,
): string | null {
  if (nameRequired && !form.name.trim()) {
    return "Give the lane a name";
  }
  if (form.hasExpiry && !form.expiresOn) {
    return "Pick an expiry date, or turn the expiry off";
  }
  return null;
}
